import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Segmented, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";

import Board from "../components/Board";
import BoardSwimlanes from "../components/BoardSwimlanes";
import IssueFormModal from "../../issues/components/IssueFormModal";
import {
  createIssue,
  fetchIssues,
  fetchSprints,
} from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveSprint,
  selectCurrentProject,
  selectIssuesByProject,
  selectIssuesLoading,
  selectStatuses,
} from "../../../../core/data/redux/projects/selectors";
import { errMsg } from "../../helpers/errMsg.helper";
import { notify } from "../../../../services/toast.config";

import type { AppDispatch } from "../../../../core/data/redux/store";

const BoardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const statuses = useSelector(selectStatuses);
  const issuesLoading = useSelector(selectIssuesLoading);
  const projectIssues = useSelector(
    selectIssuesByProject(project?.id ?? null)
  );
  const activeSprint = useSelector(selectActiveSprint);

  const [createOpen, setCreateOpen] = useState(false);
  const [createInStatusId, setCreateInStatusId] = useState<number | null>(null);
  const [createInEpicId, setCreateInEpicId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [groupBy, setGroupBy] = useState<"NONE" | "EPIC">("NONE");

  const doneStatusIds = useMemo(
    () =>
      new Set(
        statuses.filter((s) => s.category === "DONE").map((s) => s.id)
      ),
    [statuses]
  );

  const visibleIssues = useMemo(() => {
    if (!projectIssues) return [];
    if (activeSprint) {
      return projectIssues.filter(
        (i) => Number(i.sprint_id) === Number(activeSprint.id) && i.is_active
      );
    }
    return projectIssues.filter(
      (i) => i.is_active && !doneStatusIds.has(i.status_id)
    );
  }, [projectIssues, activeSprint, doneStatusIds]);

  useEffect(() => {
    if (project?.id) {
      dispatch(fetchSprints({ projectId: project.id, state: "ACTIVE" }));
      const filters: Record<string, any> = {
        project_id: project.id,
      };
      if (activeSprint) {
        filters.sprint_id = activeSprint.id;
      } else {
        filters.exclude_done = true;
      }
      dispatch(fetchIssues(filters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, project?.id, activeSprint?.id]);

  const handleCreateClick = useCallback((statusId: number) => {
    setCreateInStatusId(statusId);
    setCreateInEpicId(null);
    setCreateOpen(true);
  }, []);

  const handleSwimlaneCreateClick = useCallback(
    (statusId: number, epicId: number | null) => {
      setCreateInStatusId(statusId);
      setCreateInEpicId(epicId);
      setCreateOpen(true);
    },
    []
  );

  const handleSubmit = useCallback(
    async (payload: Record<string, any>) => {
      setSubmitting(true);
      try {
        await dispatch(createIssue(payload)).unwrap();
        if (project?.id) {
          const filters: Record<string, any> = { project_id: project.id };
          if (activeSprint) filters.sprint_id = activeSprint.id;
          else filters.exclude_done = true;
          dispatch(fetchIssues(filters));
        }
        notify.success("Issue created");
        setCreateOpen(false);
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to create issue"));
        throw e;
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch, project?.id, activeSprint?.id]
  );

  if (!project) {
    return (
      <div className="card">
        <div className="card-body text-center py-4">
          <Spin />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card border-0 mb-3" style={{ borderRadius: 12, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
        <div className="card-header border-0">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h4 className="mb-1 d-flex align-items-center gap-2" style={{ whiteSpace: "nowrap" }}>
                <i className="ti ti-layout-board" style={{ color: "#3b82f6", fontSize: 22, flexShrink: 0 }} />
                Board
              </h4>
              <span className="text-muted small">
                {activeSprint
                  ? `Active sprint: ${activeSprint.name}`
                  : "All open issues (no active sprint)"}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Segmented
                value={groupBy}
                onChange={(v) => setGroupBy(v as "NONE" | "EPIC")}
                options={[
                  {
                    value: "NONE",
                    label: (
                      <div style={{ padding: "2px 10px", fontWeight: 500, fontSize: 13 }}>
                        <i className="ti ti-layout-columns me-2" style={{ fontSize: 14 }} />
                        No swimlanes
                      </div>
                    ),
                  },
                  {
                    value: "EPIC",
                    label: (
                      <div style={{ padding: "2px 10px", fontWeight: 500, fontSize: 13 }}>
                        <i className="ti ti-stack-2 me-2" style={{ fontSize: 14 }} />
                        Group by epic
                      </div>
                    ),
                  },
                ]}
                style={{
                  background: "#f1f5f9",
                  borderRadius: 8,
                  padding: 3,
                }}
              />
              <button
                type="button"
                className="btn btn-info btn-sm"
                onClick={() => {
                  setCreateInStatusId(null);
                  setCreateInEpicId(null);
                  setCreateOpen(true);
                }}
              >
                <i className="ti ti-plus me-1" />
                New Issue
              </button>
            </div>
          </div>
        </div>
      </div>

      {issuesLoading && visibleIssues.length === 0 ? (
        <div className="text-center py-4">
          <Spin />
        </div>
      ) : statuses.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-4">
            <p className="text-muted mb-0">
              No board columns configured. Configure columns in Board Settings.
            </p>
          </div>
        </div>
      ) : groupBy === "EPIC" ? (
        <BoardSwimlanes
          projectId={project.id}
          projectKey={project.key}
          statuses={statuses}
          issues={visibleIssues}
          onCreateIssueClick={handleSwimlaneCreateClick}
        />
      ) : (
        <Board
          projectKey={project.key}
          statuses={statuses}
          issues={visibleIssues}
          onCreateIssueClick={handleCreateClick}
        />
      )}

      <IssueFormModal
        open={createOpen}
        defaultStatusId={createInStatusId}
        defaultEpicId={createInEpicId}
        defaultSprintId={activeSprint?.id ?? null}
        submitting={submitting}
        onSubmit={handleSubmit}
        onCancel={() => setCreateOpen(false)}
      />
    </div>
  );
};

export default BoardPage;
